import ListPageController, { type StatCardDef } from "./mixins/ListPageController";
import Fragment from "sap/ui/core/Fragment";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import JSONModel from "sap/ui/model/json/JSONModel";
import type { SearchField$LiveChangeEvent, SearchField$SearchEvent } from "sap/m/SearchField";
import type { Select$ChangeEvent } from "sap/m/Select";
import type Dialog from "sap/m/Dialog";
import type Input from "sap/m/Input";
import type Text from "sap/m/Text";
import type Table from "sap/m/Table";
import type ColumnListItem from "sap/m/ColumnListItem";
import type Context from "sap/ui/model/odata/v4/Context";
import type ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import type { Button$PressEvent } from "sap/m/Button";
import type { ListBase$SelectionChangeEvent } from "sap/m/ListBase";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type { ResponseError } from "../types/Errors";
import { problemMessage } from "../util/ProblemDetails";

// === List view types ===

interface OrderRow {
	Id: string;
	OrderNumber: string;
	OrderType: string;
	Status: string;
	PaymentStatus: string;
	BeneficiaryOrgId: string;
	OrderDate: string;
	ValidUntil: string;
}

type StatusKey = "ALL" | "Placed" | "Approved" | "Fulfilled" | "Voided";
type PaymentKey = "ALL" | "Unpaid" | "Paid" | "PaymentOverdue";

interface ViewState {
	statusFilter: StatusKey;
	paymentFilter: PaymentKey;
	searchQuery: string;
}

interface VariantPreset {
	status: StatusKey;
	payment: PaymentKey;
	search: string;
}

const VARIANTS: Record<string, VariantPreset> = {
	"standard":       { status: "ALL",    payment: "ALL",            search: "" },
	"pendingPayment": { status: "ALL",    payment: "Unpaid",         search: "" },
	"paid":           { status: "ALL",    payment: "Paid",           search: "" },
	"overdue":        { status: "ALL",    payment: "PaymentOverdue", search: "" },
	"voided":         { status: "Voided", payment: "ALL",            search: "" }
};

const STATS: readonly StatCardDef[] = [
	{ key: "total", labelKey: "orders.stats.total.label", subtitleKey: "orders.stats.total.subtitle", state: "Neutral" },
	{ key: "placed", labelKey: "orders.stats.placed.label", subtitleKey: "orders.stats.placed.subtitle", state: "Critical", filter: "Status eq 'Placed'" },
	{ key: "approved", labelKey: "orders.stats.approved.label", subtitleKey: "orders.stats.approved.subtitle", state: "Good", filter: "Status eq 'Approved'" },
	{ key: "overdue", labelKey: "orders.stats.overdue.label", subtitleKey: "orders.stats.overdue.subtitle", state: "Error", filter: "PaymentStatus eq 'PaymentOverdue'" }
];

// === Add Order Dialog types ===

interface ListItemsHost {
	getBinding(name: string): ODataListBinding | undefined;
}

type BeneficiaryTypeFilter = "ALL" | "Partner" | "EndCustomer";
type IssuerMode = "CompuTec" | "Partner";

interface BeneficiaryPickerState {
	typeFilter: BeneficiaryTypeFilter;
	searchQuery: string;
	selectedId: string;
	selectedName: string;
	selectedType: string;
}

interface IssuerPickerState {
	searchQuery: string;
	selectedId: string;
	selectedName: string;
	selectedPath: string;
}

interface BillingOption { key: string; text: string }

interface OrderLine {
	lineNumber: number;
	productFamilyId: string;
	billingFilter: string;
	availableBillingModes: BillingOption[];
	productId: string;
	quantity: number;
	packSize: number;
	billingMode: string;
	licensingMode: string;
	billingPeriodDays: number | null;
	isLocationAware: boolean;
	locationId: string;
}

interface OrderNewState {
	orderType: string;
	issuerMode: IssuerMode;
	issuerOrgId: string;
	issuerPath: string;
	issuerDisplay: string;
	issuerValueState: "None" | "Error";
	beneficiaryOrgId: string;
	beneficiaryDisplay: string;
	beneficiaryValueState: "None" | "Error";
	relatedOrderId: string;
	orderDate: string;
	validUntil: string;
	demoValidUntilOverride: string;
	notes: string;
	defaultLocationId: string;
	lines: OrderLine[];
	step1Valid: boolean;
	step2Valid: boolean;
}

interface PrereqResponse {
	requiredProductId: string;
	minQuantity: number;
	maxRatio: number | null;
	requiredProductCode: string;
	requiredProductName: string;
	requiredProductFamilyId: string;
	requiredProductBillingMode: string;
	requiredProductLicensingMode: string;
	requiredProductPackSize: number;
	requiredProductBillingPeriodDays: number | null;
}

interface CreateOrderRequest {
	orderType: string;
	relatedOrderId: string | null;
	orderDate: string;
	validUntil: string | null;
	beneficiaryOrgId: string;
	lines: { productId: string; lineNumber: number; quantity: number; locationId: string | null }[];
	notes: string | null;
	demoValidUntilOverride?: string | null;
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * @namespace computec.b2b.portal.controller
 */
export default class Orders extends ListPageController {
	// Pay dialog
	private payDialog?: Dialog;
	private payTargetOrderId?: string;

	// Add Order dialog + pickers
	private addOrderDialog?: Dialog;
	private benePickerDialog?: Dialog;
	private issuerPickerDialog?: Dialog;
	private computecOrgId?: string;
	private prereqCache: Map<string, PrereqResponse[]> = new Map();

	public override onInit(): void {
		const initial: ViewState = {
			statusFilter: "ALL",
			paymentFilter: "ALL",
			searchQuery: ""
		};
		this.getView().setModel(new JSONModel(initial), "view");
		void this.initStatsCards("OrderReports", STATS);
		this.getRouter().getRoute("orders")?.attachPatternMatched(() => { void this.onShow(); });
		// Skrót "Złóż zamówienie" z karty organizacji — otwiera dialog z prefillem beneficjenta.
		this.getRouter().getRoute("orderNewFor")?.attachPatternMatched((e) => { void this.onShowForBeneficiary(e); });
	}

	/**
	 * Wejście z route <c>orderNewFor</c> (skrót "Złóż zamówienie" z karty org). Renderuje listę
	 * jak zwykłe <c>orders</c>, a następnie otwiera dialog nowego zamówienia z prefillem beneficjenta.
	 */
	private async onShowForBeneficiary(event: Route$PatternMatchedEvent): Promise<void> {
		await this.onShow();
		const args = event.getParameter("arguments") as { beneficiaryId?: string } | undefined;
		const beneficiaryId = args?.beneficiaryId ?? "";
		if (UUID_RE.test(beneficiaryId)) {
			await this.openAddOrderForBeneficiary(beneficiaryId);
		}
	}

	private async onShow(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const title = bundle.getText("orders.title") ?? "Orders";
		this.setPageHeader({ title, subtitle: bundle.getText("orders.subtitle") ?? "" });
		this.applyFilters();
		void this.refreshStatsCards();
	}

	public onSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("view") as JSONModel).setProperty("/searchQuery", raw);
		this.applyFilters();
	}

	public onStatusSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as StatusKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/statusFilter", key);
		this.applyFilters();
	}

	public onPaymentSelect(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as PaymentKey;
		(this.getView().getModel("view") as JSONModel).setProperty("/paymentFilter", key);
		this.applyFilters();
	}

	public onVariantSelect(event: Select$ChangeEvent): void {
		const key = event.getParameter("selectedItem")?.getKey() ?? "standard";
		const preset = VARIANTS[key];
		if (!preset) {
			return;
		}
		const model = this.getView().getModel("view") as JSONModel;
		model.setProperty("/statusFilter", preset.status);
		model.setProperty("/paymentFilter", preset.payment);
		model.setProperty("/searchQuery", preset.search);
		this.applyFilters();
	}

	public onRefresh(): void {
		this.refreshTable("orderTable");
		void this.refreshStatsCards();
	}

	private applyFilters(): void {
		const state = (this.getView().getModel("view") as JSONModel).getData() as ViewState;
		const parts: string[] = [];

		if (state.searchQuery.trim().length > 0) {
			const q = escapeOData(state.searchQuery.trim());
			parts.push(`contains(OrderNumber,'${q}')`);
		}
		if (state.statusFilter !== "ALL") {
			parts.push(`Status eq '${state.statusFilter}'`);
		}
		if (state.paymentFilter !== "ALL") {
			parts.push(`PaymentStatus eq '${state.paymentFilter}'`);
		}

		const binding = this.getTableBinding("orderTable");
		binding?.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}

	public onOpenOrderDetail(event: Button$PressEvent): void {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		if (!ctx) {
			return;
		}
		const id = ctx.getProperty("Id") as string | undefined;
		if (!id) {
			return;
		}
		this.navTo("orderDetail", { id });
	}

	public onOpenPartner(event: Button$PressEvent): void {
		this.openOrgFromOrderRow(event, "PartnerOrgId");
	}

	public onOpenBeneficiary(event: Button$PressEvent): void {
		this.openOrgFromOrderRow(event, "BeneficiaryOrgId");
	}

	private openOrgFromOrderRow(event: Button$PressEvent, property: "PartnerOrgId" | "BeneficiaryOrgId"): void {
		const ctx = event.getSource().getBindingContext("odata") as Context | null;
		const id = ctx?.getProperty(property) as string | undefined;
		if (!id) {
			return;
		}
		this.navToOrganization(id);
	}

	// === Pay flow ===

	public async onPayPress(event: Button$PressEvent): Promise<void> {
		const button = event.getSource();
		const ctx = button.getBindingContext("odata") as Context | null;
		if (!ctx) {
			return;
		}
		const row = ctx.getObject() as OrderRow;
		this.payTargetOrderId = row.Id;

		if (!this.payDialog) {
			this.payDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.PayOrderDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.payDialog);
		}

		const bundle = await this.getResourceBundle();
		const info = bundle.getText("orders.pay.info", [row.OrderNumber, row.OrderType]) ?? `Pay ${row.OrderNumber}`;
		(this.byId("payOrderInfo") as Text).setText(info);
		const invoiceInput = this.byId("inpInvoiceNumber") as Input;
		invoiceInput.setValue("");
		invoiceInput.setValueState("None");
		invoiceInput.setValueStateText("");
		this.payDialog.open();
	}

	public onInvoiceNumberChange(): void {
		const invoiceInput = this.byId("inpInvoiceNumber") as Input;
		if (invoiceInput.getValue().trim().length > 0) {
			invoiceInput.setValueState("None");
			invoiceInput.setValueStateText("");
		}
	}

	public onCancelPay(): void {
		this.payDialog?.close();
		this.payTargetOrderId = undefined;
	}

	public async onConfirmPay(): Promise<void> {
		if (!this.payDialog || !this.payTargetOrderId) {
			return;
		}
		const invoiceInput = this.byId("inpInvoiceNumber") as Input;
		const invoiceNumber = invoiceInput.getValue().trim();
		const bundle = await this.getResourceBundle();

		if (invoiceNumber.length === 0) {
			invoiceInput.setValueState("Error");
			invoiceInput.setValueStateText(bundle.getText("orders.pay.invoiceNumberRequired") ?? "Invoice number is required");
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json<void>(`/api/v1/orders/${this.payTargetOrderId}/payments`, {
				method: "POST",
				body: JSON.stringify({ invoiceNumber })
			});
			MessageToast.show(bundle.getText("orders.pay.success") ?? "Payment registered");
			this.payDialog.close();
			this.payTargetOrderId = undefined;
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("orders.pay.failure") ?? "Failed to register payment"));
		}
	}

	// === Add Order dialog ===

	public async onAddOrderPress(): Promise<void> {
		if (!this.addOrderDialog) {
			this.addOrderDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AddOrderDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.addOrderDialog);
		}
		this.prereqCache.clear();
		this.resetAddOrderState();
		this.addOrderDialog.open();
	}

	/**
	 * Otwiera dialog nowego zamówienia z prefillowanym beneficjentem (id organizacji z karty org).
	 * Wystawca (issuer) pozostaje domyślny (CompuTec) — użytkownik może go zmienić; walidację
	 * cross-org robi backend przy zapisie. Działa dla Partner i EndCustomer (beneficjentem może
	 * być każda org poza CompuTec).
	 */
	private async openAddOrderForBeneficiary(beneficiaryId: string): Promise<void> {
		if (!this.addOrderDialog) {
			this.addOrderDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.AddOrderDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.addOrderDialog);
		}
		this.prereqCache.clear();
		this.resetAddOrderState();

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const org = await fetcher.json<{ Name: string; Type: string }>(
				`/odata/v4/Organizations(${beneficiaryId})?$select=Name,Type`
			);
			const model = this.getAddOrderModel();
			model.setProperty("/beneficiaryOrgId", beneficiaryId);
			model.setProperty("/beneficiaryDisplay", org.Type ? `${org.Name} (${org.Type})` : org.Name);
			this.recomputeValidity();
		} catch (e) {
			console.warn("Orders.openAddOrderForBeneficiary: failed to prefill beneficiary", e);
		}

		this.addOrderDialog.open();
	}

	private resetAddOrderState(): void {
		const today = new Date().toISOString().substring(0, 10);
		const inOneYear = new Date();
		inOneYear.setFullYear(inOneYear.getFullYear() + 1);
		const validUntil = inOneYear.toISOString().substring(0, 10);

		const initial: OrderNewState = {
			orderType: "Initial",
			issuerMode: "CompuTec",
			issuerOrgId: "",
			issuerPath: "",
			issuerDisplay: "",
			issuerValueState: "None",
			beneficiaryOrgId: "",
			beneficiaryDisplay: "",
			beneficiaryValueState: "None",
			relatedOrderId: "",
			orderDate: today,
			validUntil: validUntil,
			demoValidUntilOverride: "",
			notes: "",
			defaultLocationId: "",
			lines: [emptyLine(1)],
			step1Valid: false,
			step2Valid: false
		};
		this.getView().setModel(new JSONModel(initial), "addOrder");

		this.getView().setModel(new JSONModel({
			typeFilter: "ALL",
			searchQuery: "",
			selectedId: "",
			selectedName: "",
			selectedType: ""
		} satisfies BeneficiaryPickerState), "benePicker");

		this.getView().setModel(new JSONModel({
			searchQuery: "",
			selectedId: "",
			selectedName: "",
			selectedPath: ""
		} satisfies IssuerPickerState), "issuerPicker");
	}

	private getAddOrderModel(): JSONModel {
		return this.getView().getModel("addOrder") as JSONModel;
	}

	public onCancelAddOrder(): void {
		this.addOrderDialog?.close();
	}

	public onChange(): void {
		this.recomputeValidity();
		this.clearInlineErrorStates();
	}

	public onOrderTypeChange(): void {
		const model = this.getAddOrderModel();
		const orderType = model.getProperty("/orderType") as string;
		if (orderType === "Demo") {
			const orderDateRaw = (model.getProperty("/orderDate") as string) || new Date().toISOString().substring(0, 10);
			const d = new Date(`${orderDateRaw}T00:00:00Z`);
			d.setUTCDate(d.getUTCDate() + 90);
			model.setProperty("/demoValidUntilOverride", d.toISOString().substring(0, 10));
		} else {
			model.setProperty("/demoValidUntilOverride", "");
		}
		model.setProperty("/beneficiaryOrgId", "");
		model.setProperty("/beneficiaryDisplay", "");
		this.recomputeValidity();
		this.clearInlineErrorStates();
	}

	public onLineFamilyChange(event: { getSource(): unknown }): void {
		const cb = event.getSource() as { getBindingContext(name: string): { getPath(): string } | null };
		const ctx = cb.getBindingContext("addOrder");
		if (!ctx) return;
		const model = this.getAddOrderModel();
		const linePath = ctx.getPath();
		const familyId = model.getProperty(`${linePath}/productFamilyId`) as string;
		model.setProperty(`${linePath}/billingFilter`, "");
		model.setProperty(`${linePath}/productId`, "");
		model.setProperty(`${linePath}/availableBillingModes`, []);
		this.resetLineSnapshot(linePath);
		this.refreshAllProductFilters();
		this.recomputeValidity();
		if (UUID_RE.test(familyId)) {
			void this.fetchBillingModesForFamily(familyId, linePath);
		}
	}

	private async fetchBillingModesForFamily(familyId: string, linePath: string): Promise<void> {
		const fetcher = this.getOwnerComponent().getAuthFetch();
		const bundle = await this.getResourceBundle();
		try {
			const r = await fetcher.fetch(
				`/odata/v4/Products?$filter=ProductFamilyId eq ${familyId} and Status eq 'Active'&$select=BillingMode&$top=1000`,
				{ headers: { Accept: "application/json" } }
			);
			if (!r.ok) {
				return;
			}
			const data = await r.json() as { value?: { BillingMode?: string }[] };
			const modes = data.value ?? [];
			const distinct = Array.from(new Set(
				modes.map(m => m.BillingMode).filter((v): v is string => typeof v === "string" && v.length > 0)
			));
			const order = ["Perpetual", "PerpetualWithMaintenance", "SubscriptionMonthly", "SubscriptionAnnual"];
			distinct.sort((a, b) => order.indexOf(a) - order.indexOf(b));
			const options: BillingOption[] = distinct.map(key => ({
				key,
				text: bundle.getText(`billingMode.${key}`) ?? key
			}));
			const model = this.getAddOrderModel();
			if (model.getProperty(`${linePath}/productFamilyId`) === familyId) {
				model.setProperty(`${linePath}/availableBillingModes`, options);
			}
		} catch {
			/* fail silently */
		}
	}

	public onLineBillingChange(event: { getSource(): unknown }): void {
		const cb = event.getSource() as { getBindingContext(name: string): { getPath(): string } | null };
		const ctx = cb.getBindingContext("addOrder");
		if (!ctx) return;
		const model = this.getAddOrderModel();
		const linePath = ctx.getPath();
		model.setProperty(`${linePath}/productId`, "");
		this.resetLineSnapshot(linePath);
		this.refreshAllProductFilters();
		this.recomputeValidity();
	}

	private resetLineSnapshot(linePath: string): void {
		const model = this.getAddOrderModel();
		model.setProperty(`${linePath}/packSize`, 1);
		model.setProperty(`${linePath}/billingMode`, "");
		model.setProperty(`${linePath}/licensingMode`, "");
		model.setProperty(`${linePath}/billingPeriodDays`, null);
		model.setProperty(`${linePath}/isLocationAware`, false);
		model.setProperty(`${linePath}/locationId`, "");
	}

	public onLineLocationChange(): void {
		this.recomputeValidity();
	}

	public onDefaultLocationChange(): void {
		const model = this.getAddOrderModel();
		const defaultLoc = (model.getProperty("/defaultLocationId") as string) || "";
		if (defaultLoc) {
			const lines = (model.getProperty("/lines") as OrderLine[] | undefined) ?? [];
			lines.forEach((line, idx) => {
				if (line.isLocationAware && !line.locationId) {
					model.setProperty(`/lines/${idx}/locationId`, defaultLoc);
				}
			});
		}
		this.recomputeValidity();
	}

	public onLineProductChange(event: { getSource(): unknown }): void {
		const cb = event.getSource() as {
			getBindingContext(name: string): { getPath(): string } | null;
			getSelectedItem(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null } | null;
		};
		const ctx = cb.getBindingContext("addOrder");
		if (!ctx) {
			this.recomputeValidity();
			return;
		}
		const path = ctx.getPath();
		const idx = parseInt(path.substring(path.lastIndexOf("/") + 1), 10);
		const lines = this.getAddOrderModel().getProperty("/lines") as OrderLine[];

		const model = this.getAddOrderModel();
		const sel = cb.getSelectedItem();
		const productCtx = sel?.getBindingContext("odata");
		const linePath = `/lines/${idx}`;
		if (productCtx) {
			const productId = (productCtx.getProperty("Id") as string | undefined) ?? "";
			const productName = (productCtx.getProperty("Name") as string | undefined) ?? "";
			if (UUID_RE.test(productId)) {
				void this.fetchProductSnapshot(productId, linePath, productName);
			}
		} else {
			model.setProperty(`${linePath}/packSize`, 1);
			model.setProperty(`${linePath}/billingMode`, "");
			model.setProperty(`${linePath}/licensingMode`, "");
			model.setProperty(`${linePath}/billingPeriodDays`, null);
			model.setProperty(`${linePath}/isLocationAware`, false);
			model.setProperty(`${linePath}/locationId`, "");
		}

		this.refreshAllProductFilters();

		const lastIndex = lines.length - 1;
		if (idx === lastIndex && Number.isFinite(idx)) {
			const current = lines[idx];
			if (UUID_RE.test(current.productId) && Number(current.quantity) >= 1) {
				this.onAddLine();
				this.clearInlineErrorStates();
				return;
			}
		}
		this.recomputeValidity();
		this.clearInlineErrorStates();
	}

	private refreshAllProductFilters(): void {
		const table = this.byId("tblLines") as { getItems?(): { getCells(): unknown[] }[] } | undefined;
		const items = table?.getItems?.() ?? [];
		const lines = (this.getAddOrderModel().getProperty("/lines") ?? []) as OrderLine[];
		const pickedIds = lines.map(l => l.productId).filter(id => UUID_RE.test(id));

		items.forEach((item, idx) => {
			const cells = item.getCells();
			// cells layout: [#, Family, Billing, Product, Pack, Qty, Total, Location, Mode, Period, Del]
			const productCb = cells[3] as { getBinding(name: string): ODataListBinding | undefined } | undefined;
			const binding = productCb?.getBinding("items");
			if (!binding) return;

			const line = lines[idx];
			if (!line) return;

			const parts: string[] = ["Status eq 'Active'"];
			if (UUID_RE.test(line.productFamilyId)) {
				parts.push(`ProductFamilyId eq ${line.productFamilyId}`);
			}
			if (line.billingFilter) {
				parts.push(`BillingMode eq '${line.billingFilter}'`);
			}
			pickedIds.filter(id => id !== line.productId).forEach(id => {
				parts.push(`Id ne ${id}`);
			});
			binding.changeParameters({ $filter: parts.join(" and ") });
		});
	}

	public onLoadItems(event: { getSource(): ListItemsHost }): void {
		const binding = event.getSource().getBinding("items");
		if (!binding) {
			return;
		}
		void binding.requestContexts(0, 500);
	}

	private async fetchProductSnapshot(productId: string, linePath: string, productName: string): Promise<void> {
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const r = await fetcher.fetch(
				`/odata/v4/Products(${productId})?$select=PackSize,BillingMode,LicensingMode,BillingPeriodDays,IsLocationAware`,
				{ headers: { Accept: "application/json" } }
			);
			if (!r.ok) {
				return;
			}
			const ent = await r.json() as {
				PackSize?: number;
				BillingMode?: string;
				LicensingMode?: string;
				BillingPeriodDays?: number | null;
				IsLocationAware?: boolean;
			};
			const model = this.getAddOrderModel();
			if (model.getProperty(`${linePath}/productId`) !== productId) {
				return;
			}
			model.setProperty(`${linePath}/packSize`, ent.PackSize ?? 1);
			model.setProperty(`${linePath}/billingMode`, ent.BillingMode ?? "");
			model.setProperty(`${linePath}/licensingMode`, ent.LicensingMode ?? "");
			model.setProperty(`${linePath}/billingPeriodDays`, ent.BillingPeriodDays ?? null);
			const aware = Boolean(ent.IsLocationAware);
			model.setProperty(`${linePath}/isLocationAware`, aware);
			if (!aware) {
				model.setProperty(`${linePath}/locationId`, "");
			} else {
				const currentLine = model.getProperty(`${linePath}/locationId`) as string;
				const defaultLoc = model.getProperty("/defaultLocationId") as string;
				if (!currentLine && defaultLoc) {
					model.setProperty(`${linePath}/locationId`, defaultLoc);
				}
			}
			void this.checkAndAddPrerequisites(productId, productName);
			this.recomputeValidity();
		} catch {
			/* fail silently */
		}
	}

	// === Cross-sell: prerequisites ===

	private async fetchPrerequisites(productId: string): Promise<PrereqResponse[]> {
		const cached = this.prereqCache.get(productId);
		if (cached) {
			return cached;
		}
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const r = await fetcher.fetch(`/api/v1/products/${productId}/prerequisites`, {
				headers: { Accept: "application/json" }
			});
			if (!r.ok) {
				return [];
			}
			const data = await r.json() as PrereqResponse[];
			const list = Array.isArray(data) ? data : [];
			this.prereqCache.set(productId, list);
			return list;
		} catch {
			return [];
		}
	}

	private async checkAndAddPrerequisites(productId: string, productName: string): Promise<void> {
		const prereqs = await this.fetchPrerequisites(productId);
		if (prereqs.length === 0) {
			return;
		}
		const model = this.getAddOrderModel();
		const lines = (model.getProperty("/lines") as OrderLine[]).slice();
		const triggerLine = lines.find(l => l.productId === productId);
		const triggerBillingMode = triggerLine?.billingMode ?? "";
		const matchingByBilling = triggerBillingMode
			? prereqs.filter(p => p.requiredProductBillingMode === triggerBillingMode)
			: prereqs;

		const existingProductIds = new Set(
			lines.map(l => l.productId).filter(id => UUID_RE.test(id))
		);
		const missing = matchingByBilling.filter(p => !existingProductIds.has(p.requiredProductId));
		if (missing.length === 0) {
			return;
		}
		const lastIdx = lines.length - 1;
		const tailIsEmpty = lastIdx >= 0
			&& !UUID_RE.test(lines[lastIdx].productId)
			&& lines[lastIdx].productFamilyId === ""
			&& lines[lastIdx].billingFilter === "";

		let nextLineNumber = lines.length > 0
			? Math.max(...lines.map(l => l.lineNumber)) + 1
			: 1;

		const newLines: OrderLine[] = missing.map(p => {
			const ln = emptyLine(nextLineNumber++);
			ln.productFamilyId = p.requiredProductFamilyId;
			ln.billingFilter = p.requiredProductBillingMode;
			ln.productId = p.requiredProductId;
			ln.quantity = p.minQuantity;
			ln.packSize = p.requiredProductPackSize;
			ln.billingMode = p.requiredProductBillingMode;
			ln.licensingMode = p.requiredProductLicensingMode;
			ln.billingPeriodDays = p.requiredProductBillingPeriodDays;
			return ln;
		});

		const insertIndex = tailIsEmpty ? lastIdx : lines.length;
		if (tailIsEmpty) {
			lines.splice(lastIdx, 0, ...newLines);
		} else {
			lines.push(...newLines);
		}
		model.setProperty("/lines", lines);
		this.refreshAllProductFilters();
		this.recomputeValidity();

		newLines.forEach((ln, i) => {
			void this.fetchBillingModesForFamily(ln.productFamilyId, `/lines/${insertIndex + i}`);
		});

		const bundle = await this.getResourceBundle();
		const first = missing[0];
		const text = bundle.getText("orders.new.toast.prereqAdded", [first.requiredProductName, productName])
			?? `${first.requiredProductName} added as prerequisite for ${productName}`;
		MessageToast.show(text);
	}

	// === Issuer value-help picker ===

	private async ensureComputecOrgId(): Promise<string | undefined> {
		if (this.computecOrgId) {
			return this.computecOrgId;
		}
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const r = await fetcher.fetch(
				"/odata/v4/Organizations?$filter=Type%20eq%20'CompuTec'&$select=Id&$top=1",
				{ headers: { Accept: "application/json" } }
			);
			if (!r.ok) {
				return undefined;
			}
			const data = await r.json() as { value?: { Id: string }[] };
			this.computecOrgId = data.value?.[0]?.Id;
			return this.computecOrgId;
		} catch {
			return undefined;
		}
	}

	private hasIssuerChoice(): boolean {
		return this.getOwnerComponent().getPermissions().has("orgs.read-all");
	}

	public onIssuerModeChange(): void {
		const model = this.getAddOrderModel();
		model.setProperty("/issuerOrgId", "");
		model.setProperty("/issuerPath", "");
		model.setProperty("/issuerDisplay", "");
		model.setProperty("/beneficiaryOrgId", "");
		model.setProperty("/beneficiaryDisplay", "");
		this.recomputeValidity();
		this.clearInlineErrorStates();
	}

	public async onIssuerValueHelp(): Promise<void> {
		if (!this.issuerPickerDialog) {
			this.issuerPickerDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.IssuerPickerDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.issuerPickerDialog);
			this.issuerPickerDialog.attachAfterOpen(() => this.applyIssuerPickerFilters());
		}

		const state = this.getAddOrderModel().getData() as OrderNewState;
		const pickerModel = this.getView().getModel("issuerPicker") as JSONModel;
		pickerModel.setProperty("/searchQuery", "");
		pickerModel.setProperty("/selectedId", state.issuerOrgId);
		pickerModel.setProperty("/selectedName", state.issuerDisplay);
		pickerModel.setProperty("/selectedPath", state.issuerPath);

		this.applyIssuerPickerFilters();
		this.issuerPickerDialog.open();
	}

	public onIssuerPickerSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("issuerPicker") as JSONModel).setProperty("/searchQuery", raw);
		this.applyIssuerPickerFilters();
	}

	public onIssuerPickerSelectionChange(event: ListBase$SelectionChangeEvent): void {
		const item = event.getParameter("listItem") as ColumnListItem | undefined;
		const ctx = item?.getBindingContext("odata") as Context | null | undefined;
		const pickerModel = this.getView().getModel("issuerPicker") as JSONModel;
		if (!ctx) {
			pickerModel.setProperty("/selectedId", "");
			pickerModel.setProperty("/selectedName", "");
			pickerModel.setProperty("/selectedPath", "");
			return;
		}
		pickerModel.setProperty("/selectedId", String(ctx.getProperty("Id") ?? ""));
		pickerModel.setProperty("/selectedName", String(ctx.getProperty("Name") ?? ""));
		pickerModel.setProperty("/selectedPath", String(ctx.getProperty("Path") ?? ""));
	}

	public onIssuerPickerConfirm(): void {
		const picker = (this.getView().getModel("issuerPicker") as JSONModel).getData() as IssuerPickerState;
		if (!UUID_RE.test(picker.selectedId)) {
			return;
		}
		const model = this.getAddOrderModel();
		model.setProperty("/issuerOrgId", picker.selectedId);
		model.setProperty("/issuerPath", picker.selectedPath);
		model.setProperty("/issuerDisplay", picker.selectedName);
		model.setProperty("/beneficiaryOrgId", "");
		model.setProperty("/beneficiaryDisplay", "");

		this.issuerPickerDialog?.close();
		this.recomputeValidity();
		this.clearInlineErrorStates();
	}

	public onIssuerPickerCancel(): void {
		this.issuerPickerDialog?.close();
	}

	private applyIssuerPickerFilters(): void {
		const table = this.byId("issuerPickerTable") as Table | undefined;
		const binding = table?.getBinding("items") as ODataListBinding | undefined;
		if (!binding) {
			return;
		}
		const state = (this.getView().getModel("issuerPicker") as JSONModel).getData() as IssuerPickerState;
		const parts: string[] = ["Type eq 'Partner'"];
		if (state.searchQuery.trim().length > 0) {
			const qLower = escapeOData(state.searchQuery.trim().toLowerCase());
			parts.push(`contains(tolower(Name),'${qLower}')`);
		}
		binding.changeParameters({ $filter: parts.join(" and ") });
	}

	// === Beneficiary value-help picker ===

	public async onBeneficiaryValueHelp(): Promise<void> {
		if (!this.benePickerDialog) {
			this.benePickerDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.BeneficiaryPickerDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.benePickerDialog);
			this.benePickerDialog.attachAfterOpen(() => this.applyBenePickerFilters());
		}

		const state = this.getAddOrderModel().getData() as OrderNewState;
		if (this.hasIssuerChoice() && state.issuerMode === "CompuTec") {
			await this.ensureComputecOrgId();
		}

		const pickerModel = this.getView().getModel("benePicker") as JSONModel;
		pickerModel.setProperty("/typeFilter", "ALL");
		pickerModel.setProperty("/searchQuery", "");
		pickerModel.setProperty("/selectedId", state.beneficiaryOrgId);
		pickerModel.setProperty("/selectedName", state.beneficiaryDisplay);
		pickerModel.setProperty("/selectedType", "");

		this.applyBenePickerFilters();
		this.benePickerDialog.open();
	}

	public onBeneficiaryPickerSearch(event: SearchField$SearchEvent | SearchField$LiveChangeEvent): void {
		const params = event.getParameters() as { query?: string; newValue?: string };
		const raw = params.query ?? params.newValue ?? "";
		(this.getView().getModel("benePicker") as JSONModel).setProperty("/searchQuery", raw);
		this.applyBenePickerFilters();
	}

	public onBeneficiaryPickerTypeChange(event: Select$ChangeEvent): void {
		const key = (event.getParameter("selectedItem")?.getKey() ?? "ALL") as BeneficiaryTypeFilter;
		(this.getView().getModel("benePicker") as JSONModel).setProperty("/typeFilter", key);
		this.applyBenePickerFilters();
	}

	public onBeneficiaryPickerSelectionChange(event: ListBase$SelectionChangeEvent): void {
		const item = event.getParameter("listItem") as ColumnListItem | undefined;
		const ctx = item?.getBindingContext("odata") as Context | null | undefined;
		const pickerModel = this.getView().getModel("benePicker") as JSONModel;
		if (!ctx) {
			pickerModel.setProperty("/selectedId", "");
			pickerModel.setProperty("/selectedName", "");
			pickerModel.setProperty("/selectedType", "");
			return;
		}
		pickerModel.setProperty("/selectedId", String(ctx.getProperty("Id") ?? ""));
		pickerModel.setProperty("/selectedName", String(ctx.getProperty("Name") ?? ""));
		pickerModel.setProperty("/selectedType", String(ctx.getProperty("Type") ?? ""));
	}

	public onBeneficiaryPickerConfirm(): void {
		const picker = (this.getView().getModel("benePicker") as JSONModel).getData() as BeneficiaryPickerState;
		if (!UUID_RE.test(picker.selectedId)) {
			return;
		}
		const model = this.getAddOrderModel();
		model.setProperty("/beneficiaryOrgId", picker.selectedId);
		const display = picker.selectedType
			? `${picker.selectedName} (${picker.selectedType})`
			: picker.selectedName;
		model.setProperty("/beneficiaryDisplay", display);

		this.benePickerDialog?.close();
		this.recomputeValidity();
		this.clearInlineErrorStates();
	}

	public onBeneficiaryPickerCancel(): void {
		this.benePickerDialog?.close();
	}

	private applyBenePickerFilters(): void {
		if (!this.benePickerDialog) {
			return;
		}
		const table = this.byId("benePickerTable") as Table | undefined;
		const binding = table?.getBinding("items") as ODataListBinding | undefined;
		if (!binding) {
			return;
		}
		const state = (this.getView().getModel("benePicker") as JSONModel).getData() as BeneficiaryPickerState;
		const wiz = this.getAddOrderModel().getData() as OrderNewState;
		const parts: string[] = [];

		if (wiz.orderType === "Demo") {
			parts.push("(Type eq 'Partner' or Type eq 'CompuTec')");
		} else {
			const forcedType = this.forcedBeneficiaryTypeForIssuer();
			if (forcedType) {
				parts.push(`Type eq '${forcedType}'`);
			} else if (state.typeFilter !== "ALL") {
				parts.push(`Type eq '${state.typeFilter}'`);
			}

			if (this.hasIssuerChoice()) {
				if (wiz.issuerMode === "CompuTec") {
					if (this.computecOrgId) {
						parts.push(`ParentOrganizationId eq ${this.computecOrgId}`);
					}
				} else if (wiz.issuerMode === "Partner" && UUID_RE.test(wiz.issuerOrgId)) {
					// Bezpośredni klienci wybranego partnera. Filtrujemy po ParentOrganizationId
					// (jak w gałęzi CompuTec), NIE po Path — Path to kolumna ltree w Postgresie,
					// a OData startswith(Path,...) nie tłumaczy się na ltree i zwraca 500.
					parts.push(`ParentOrganizationId eq ${wiz.issuerOrgId}`);
				}
			}
		}

		if (state.searchQuery.trim().length > 0) {
			const qLower = escapeOData(state.searchQuery.trim().toLowerCase());
			parts.push(`contains(tolower(Name),'${qLower}')`);
		}
		binding.changeParameters({ $filter: parts.length > 0 ? parts.join(" and ") : undefined });
	}

	private forcedBeneficiaryTypeForIssuer(): "EndCustomer" | null {
		return this.hasIssuerChoice() ? "EndCustomer" : null;
	}

	// === Order lines ===

	public onAddLine(): void {
		const model = this.getAddOrderModel();
		const lines = (model.getProperty("/lines") as OrderLine[]).slice();
		const next = lines.length > 0 ? Math.max(...lines.map(l => l.lineNumber)) + 1 : 1;
		lines.push(emptyLine(next));
		model.setProperty("/lines", lines);
		this.recomputeValidity();
	}

	public onRemoveLine(event: Button$PressEvent): void {
		const ctx = event.getSource().getBindingContext("addOrder");
		if (!ctx) {
			return;
		}
		const model = this.getAddOrderModel();
		const lines = (model.getProperty("/lines") as OrderLine[]).slice();
		const path = ctx.getPath();
		const index = parseInt(path.substring(path.lastIndexOf("/") + 1), 10);
		if (Number.isFinite(index) && index >= 0 && index < lines.length) {
			lines.splice(index, 1);
			model.setProperty("/lines", lines);
			this.recomputeValidity();
		}
	}

	// === Submit new order ===

	public async onSubmitAddOrder(): Promise<void> {
		const model = this.getAddOrderModel();
		const state = model.getData() as OrderNewState;
		const bundle = await this.getResourceBundle();

		const errors = this.collectClientValidationErrors(state, bundle);
		if (errors.length > 0) {
			this.applyInlineErrorState(state);
			MessageBox.warning(errors.join("\n"));
			return;
		}

		const validLinesForPrereqCheck = state.lines.filter(l => UUID_RE.test(l.productId) && Number(l.quantity) >= 1);
		const prereqErrors = await this.collectPrereqViolations(validLinesForPrereqCheck, bundle);
		if (prereqErrors.length > 0) {
			MessageBox.warning(prereqErrors.join("\n"));
			return;
		}

		const validLines = state.lines.filter(l => UUID_RE.test(l.productId) && Number(l.quantity) >= 1);
		const partialLines = state.lines.filter(l =>
			UUID_RE.test(l.productId) && !(Number(l.quantity) >= 1)
		);
		if (partialLines.length > 0) {
			const proceed = await this.confirmSkipPartialLines(partialLines.length, bundle);
			if (!proceed) {
				return;
			}
		}

		const isDemo = state.orderType === "Demo";
		const payload: CreateOrderRequest = {
			orderType: state.orderType,
			relatedOrderId: state.orderType === "Initial" || isDemo ? null : state.relatedOrderId,
			orderDate: state.orderDate,
			validUntil: isDemo ? null : state.validUntil,
			beneficiaryOrgId: state.beneficiaryOrgId,
			lines: validLines.map(l => ({
				productId: l.productId,
				lineNumber: l.lineNumber,
				quantity: Number(l.quantity),
				locationId: l.isLocationAware && UUID_RE.test(l.locationId) ? l.locationId : null
			})),
			notes: state.notes.trim().length > 0 ? state.notes.trim() : null,
			demoValidUntilOverride: isDemo && state.demoValidUntilOverride.length > 0
				? state.demoValidUntilOverride
				: null
		};

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json("/api/v1/orders", {
				method: "POST",
				body: JSON.stringify(payload)
			});
			MessageToast.show(bundle.getText("orders.wizard.success") ?? "Order placed");
			this.getOwnerComponent().getModel("odata")?.refresh();
			this.addOrderDialog?.close();
			this.onRefresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("orders.wizard.failure") ?? "Failed to place order"));
		}
	}

	// === Validation helpers ===

	private recomputeValidity(): void {
		const model = this.getAddOrderModel();
		if (!model) return;
		const state = model.getData() as OrderNewState;

		const beneficiaryOk = UUID_RE.test(state.beneficiaryOrgId);
		const relatedOk = state.orderType === "Initial" || state.orderType === "Demo"
			? true
			: UUID_RE.test(state.relatedOrderId);
		const datesOk = state.orderType === "Demo"
			? state.orderDate.length > 0
				&& (state.demoValidUntilOverride.length === 0 || state.demoValidUntilOverride >= state.orderDate)
			: state.orderDate.length > 0
				&& state.validUntil.length > 0
				&& state.validUntil >= state.orderDate;
		const issuerOk = !this.hasIssuerChoice()
			|| state.issuerMode === "CompuTec"
			|| (state.issuerMode === "Partner" && UUID_RE.test(state.issuerOrgId));
		const step1 = beneficiaryOk && relatedOk && datesOk && issuerOk;

		const lines = state.lines;
		const numbers = new Set<number>();
		const step2 = lines.length > 0 && lines.every(l => {
			if (!UUID_RE.test(l.productId) || !(Number(l.quantity) >= 1)) {
				return false;
			}
			if (numbers.has(l.lineNumber)) {
				return false;
			}
			numbers.add(l.lineNumber);
			return true;
		});

		model.setProperty("/step1Valid", step1);
		model.setProperty("/step2Valid", step2);
	}

	private collectClientValidationErrors(
		state: OrderNewState,
		bundle: { getText(key: string): string | undefined }
	): string[] {
		const errors: string[] = [];

		if (this.hasIssuerChoice() && state.issuerMode === "Partner" && !UUID_RE.test(state.issuerOrgId)) {
			errors.push(bundle.getText("orders.new.issuer.error.partnerRequired") ?? "Pick the partner you are placing this order on behalf of");
		}
		if (!UUID_RE.test(state.beneficiaryOrgId)) {
			errors.push(bundle.getText("orders.wizard.error.beneficiaryRequired") ?? "Beneficiary is required");
		}
		if (state.orderType !== "Initial" && state.orderType !== "Demo" && !UUID_RE.test(state.relatedOrderId)) {
			errors.push(bundle.getText("orders.wizard.error.relatedRequired") ?? "Related order is required");
		}
		if (state.orderType === "Demo") {
			if (!state.orderDate || (state.demoValidUntilOverride.length > 0 && state.demoValidUntilOverride < state.orderDate)) {
				errors.push(bundle.getText("orders.wizard.error.datesInvalid") ?? "Order date and valid-until must be set (valid until ≥ order date)");
			}
		} else if (!state.orderDate || !state.validUntil || state.validUntil < state.orderDate) {
			errors.push(bundle.getText("orders.wizard.error.datesInvalid") ?? "Order date and valid-until must be set (valid until ≥ order date)");
		}

		const validLines = state.lines.filter(l => UUID_RE.test(l.productId) && Number(l.quantity) >= 1);
		if (validLines.length === 0) {
			errors.push(bundle.getText("orders.wizard.error.linesRequired") ?? "At least one line with product and quantity is required");
		}

		const missingLocation = validLines.filter(l => l.isLocationAware && !UUID_RE.test(l.locationId));
		if (missingLocation.length > 0) {
			const lineNumbers = missingLocation.map(l => l.lineNumber).join(", ");
			const template = bundle.getText("orders.wizard.error.locationRequired")
				?? "Lines {0} require Location to be selected (product is location-aware)";
			errors.push(template.replace("{0}", lineNumbers));
		}

		return errors;
	}

	private async collectPrereqViolations(
		lines: OrderLine[],
		bundle: { getText(key: string, params?: unknown[]): string | undefined }
	): Promise<string[]> {
		const violations: string[] = [];
		const productIds = new Set(lines.map(l => l.productId));
		await Promise.all(
			Array.from(productIds).filter(id => !this.prereqCache.has(id)).map(id => this.fetchPrerequisites(id))
		);
		for (const line of lines) {
			const prereqs = this.prereqCache.get(line.productId) ?? [];
			const relevant = line.billingMode
				? prereqs.filter(p => p.requiredProductBillingMode === line.billingMode)
				: prereqs;
			for (const p of relevant) {
				if (!productIds.has(p.requiredProductId)) {
					const text = bundle.getText("orders.new.error.prereqMissing", [line.productId, p.requiredProductName])
						?? `${line.productId} requires ${p.requiredProductName}`;
					violations.push(text);
				}
			}
		}
		return violations;
	}

	private confirmSkipPartialLines(count: number, bundle: { getText(key: string, params?: unknown[]): string | undefined }): Promise<boolean> {
		return new Promise(resolve => {
			const title = bundle.getText("orders.new.confirm.partialLines.title") ?? "Skip incomplete lines?";
			const text = bundle.getText("orders.new.confirm.partialLines.text", [count])
				?? `${count} incomplete order line(s) will be skipped. Continue?`;
			const okAction = MessageBox.Action.OK as unknown as string;
			MessageBox.confirm(text, {
				title,
				actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
				emphasizedAction: MessageBox.Action.OK,
				onClose: (action: string) => resolve(action === okAction)
			});
		});
	}

	private clearInlineErrorStates(): void {
		const model = this.getAddOrderModel();
		if (!model) return;
		model.setProperty("/beneficiaryValueState", "None");
		model.setProperty("/issuerValueState", "None");
		const lineProducts = this.byId("tblLines") as unknown as { getItems?(): { getCells(): unknown[] }[] } | undefined;
		const items = lineProducts?.getItems?.() ?? [];
		items.forEach(item => {
			const cells = item.getCells();
			(cells[3] as { setValueState(s: string): void } | undefined)?.setValueState("None");
			(cells[5] as { setValueState(s: string): void } | undefined)?.setValueState("None");
		});
	}

	private applyInlineErrorState(state: OrderNewState): void {
		const model = this.getAddOrderModel();
		model.setProperty(
			"/beneficiaryValueState",
			UUID_RE.test(state.beneficiaryOrgId) ? "None" : "Error"
		);
		const issuerError = this.hasIssuerChoice()
			&& state.issuerMode === "Partner"
			&& !UUID_RE.test(state.issuerOrgId);
		model.setProperty("/issuerValueState", issuerError ? "Error" : "None");
		const lineProducts = this.byId("tblLines") as unknown as { getItems?(): { getCells(): unknown[] }[] } | undefined;
		const items = lineProducts?.getItems?.() ?? [];
		items.forEach((item, idx) => {
			const cells = item.getCells();
			const productCb = cells[3] as { setValueState(s: string): void } | undefined;
			const qtyInput = cells[5] as { setValueState(s: string): void } | undefined;
			const line = state.lines[idx];
			if (!line) return;
			if (productCb && !UUID_RE.test(line.productId)) productCb.setValueState("Error");
			if (qtyInput && !(Number(line.quantity) >= 1)) qtyInput.setValueState("Error");
		});
	}

}

function escapeOData(value: string): string {
	return value.replace(/'/g, "''");
}

function emptyLine(lineNumber: number): OrderLine {
	return {
		lineNumber,
		productFamilyId: "",
		billingFilter: "",
		availableBillingModes: [],
		productId: "",
		quantity: 1,
		packSize: 1,
		billingMode: "",
		licensingMode: "",
		billingPeriodDays: null,
		isLocationAware: false,
		locationId: ""
	};
}
